/**
 * Shared utility for creating URL-friendly species slugs
 *
 * Must match backend slug generation in:
 * frogger1/frogger/store/sitemaps.py::CareGuideSitemap._create_species_slug
 *
 * Format: {genus}-{species}-{locality}
 * Example: dendrobates-tinctorius-azureus
 *          dendrobates-tinctorius-peacock
 *          phyllobates-terribilis-orange-blackfoot
 */

/**
 * Create URL-friendly slug from species data
 * @param {Object} species - Species object with genus, species, locality
 * @returns {string} URL slug (lowercase, hyphen-separated)
 */
export const createSpeciesSlug = (species) => {
  const genus = species?.genus || '';
  const speciesName = species?.species || '';
  const locality = species?.locality || '';

  // Build slug with lowercase components
  let slug = `${genus}-${speciesName}`.toLowerCase();

  if (locality) {
    // CRITICAL: Convert locality to lowercase BEFORE appending
    // This prevents 'Peacock' from becoming 'eacock' after regex
    slug += `-${locality.toLowerCase()}`;
  }

  // Replace non-alphanumeric with hyphens, collapse multiple hyphens, trim
  slug = slug.replace(/[^a-z0-9-]/g, '-')   // Replace special chars with hyphen
             .replace(/-+/g, '-')             // Collapse multiple hyphens
             .replace(/^-|-$/g, '');          // Remove leading/trailing hyphens

  return slug;
};

/**
 * Create genus URL path
 * @param {string} genus - Genus name (e.g., "Dendrobates", "Bombina")
 * @returns {string} URL path (e.g., "/frogs/dendrobates")
 */
export const createGenusPath = (genus) => {
  if (!genus) return '/frogs';
  return `/frogs/${genus.toLowerCase()}`;
};

/**
 * Create care guide URL path
 * @param {Object} species - Species object with genus, species, locality
 * @returns {string} URL path (e.g., "/care-guides/dendrobates-tinctorius-azureus")
 */
export const createCareGuidePath = (species) => {
  const slug = createSpeciesSlug(species);
  return `/care-guides/${slug}`;
};

/**
 * Create product URL path
 * @param {string} productId - Product UUID
 * @returns {string} URL path (e.g., "/frogs/products/86d6bce9-...")
 */
export const createProductPath = (productId) => {
  return `/frogs/products/${productId}`;
};
